import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Music, Star, Target, User, Mail, Lock } from "lucide-react";

const GENRES = [
  "Pop", "Rock", "Jazz", "Classical", "R&B", 
  "Country", "Musical Theatre", "Opera", "Soul"
];

const LEVELS = [
  { value: "beginner", label: "Beginner", description: "Just starting out" },
  { value: "intermediate", label: "Intermediate", description: "Some experience" },
  { value: "advanced", label: "Advanced", description: "Experienced singer" },
  { value: "professional", label: "Professional", description: "Performance ready" }
];

const STORAGE_KEY = "vocalzing_first_visit";
const PREFERENCES_KEY = "vocalzing_preferences";

export const FirstTimeModal = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"preferences" | "signup">("preferences");
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [level, setLevel] = useState<string>("");
  const [goals, setGoals] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const hasVisited = localStorage.getItem(STORAGE_KEY);
    
    if (!hasVisited) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handlePreferencesNext = () => {
    if (selectedGenres.length === 0 || !level) {
      toast({
        title: "Please complete your preferences",
        description: "Select at least one genre and your experience level, or skip to sign up",
        variant: "destructive"
      });
      return;
    }

    // Save preferences to localStorage
    const preferences = {
      genres: selectedGenres,
      level,
      goals
    };
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
    
    setStep("signup");
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !fullName) {
      toast({
        title: "Please fill all fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) throw error;

      if (data.user) {
        // Get preferences from localStorage
        const preferencesStr = localStorage.getItem(PREFERENCES_KEY);
        if (preferencesStr) {
          const preferences = JSON.parse(preferencesStr);
          
          // Save preferences to database
          const { error: profileError } = await supabase
            .from("profiles")
            .update({
              singing_genres: preferences.genres,
              singing_level: preferences.level,
              singing_goals: preferences.goals,
              preferences_completed: true
            })
            .eq("id", data.user.id);

          if (profileError) {
            console.error("Error saving preferences:", profileError);
          }

          // Clear preferences from localStorage
          localStorage.removeItem(PREFERENCES_KEY);
        }

        // Mark as visited
        localStorage.setItem(STORAGE_KEY, "true");
        
        toast({
          title: "Welcome to Vocalzing!",
          description: "Your account has been created successfully."
        });

        setOpen(false);
        navigate("/booking");
      }
    } catch (error: any) {
      toast({
        title: "Error creating account",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
  };

  const handleGoogleSignIn = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
      
      // Save preferences if they exist
      const preferencesStr = localStorage.getItem(PREFERENCES_KEY);
      if (preferencesStr) {
        // Preferences will be saved after OAuth redirect via the auth state change handler
        localStorage.setItem(STORAGE_KEY, "true");
      }
    } catch (error: any) {
      toast({
        title: "Error signing in with Google",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-background via-background to-secondary/5 shadow-2xl">
        {step === "preferences" ? (
          <>
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Welcome to Vocalzing!
              </DialogTitle>
              <DialogDescription className="text-base">
                Let's personalize your vocal journey. Tell us about your musical preferences.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Genres */}
              <Card className="p-6 bg-gradient-to-br from-card to-secondary/5 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-secondary">
                    <Music className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">Musical Genres</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Select the genres you're interested in (choose multiple)
                </p>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map((genre) => (
                    <Button
                      key={genre}
                      type="button"
                      variant={selectedGenres.includes(genre) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleGenre(genre)}
                    >
                      {genre}
                    </Button>
                  ))}
                </div>
              </Card>

              {/* Experience Level */}
              <Card className="p-6 bg-gradient-to-br from-card to-secondary/5 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-secondary">
                    <Star className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">Experience Level</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {LEVELS.map((lvl) => (
                    <button
                      key={lvl.value}
                      type="button"
                      onClick={() => setLevel(lvl.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-colors ${
                        level === lvl.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="font-semibold">{lvl.label}</div>
                      <div className="text-sm text-muted-foreground">{lvl.description}</div>
                    </button>
                  ))}
                </div>
              </Card>

              {/* Goals */}
              <Card className="p-6 bg-gradient-to-br from-card to-secondary/5 hover:shadow-lg transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-secondary">
                    <Target className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">Your Goals (Optional)</h3>
                </div>
                <Textarea
                  placeholder="What do you hope to achieve? (e.g., improve range, perform confidently, etc.)"
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  rows={3}
                />
              </Card>
            </div>

            <div className="flex justify-between gap-3">
              <Button variant="ghost" onClick={() => setStep("signup")}>
                Skip to Sign Up
              </Button>
              <Button onClick={handlePreferencesNext}>
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Create Your Account
              </DialogTitle>
              <DialogDescription className="text-base">
                Sign up to save your preferences and start booking lessons
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSignup} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setStep("preferences")} className="flex-1">
                  Back
                </Button>
                <Button type="submit" variant="hero" disabled={isSubmitting} className="flex-1">
                  {isSubmitting ? "Creating..." : "Sign Up"}
                </Button>
              </div>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={isSubmitting}
              className="w-full"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Sign up with Google
            </Button>

            <p className="text-sm text-center text-muted-foreground mt-4">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/auth");
                }}
                className="text-primary hover:underline font-medium"
              >
                Sign in
              </button>
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
